import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IconDirective } from '@coreui/icons-angular';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  ImaginusService,
  ImaginusSessionSearchHit,
} from '../../service/imaginus.service';
import { LocaleService } from '../../service/locale.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-imaginus-session-search',
  standalone: true,
  imports: [CommonModule, FormsModule, IconDirective, TranslateModule],
  templateUrl: './imaginus-session-search.component.html',
  styleUrls: ['./imaginus-session-search.component.scss'],
})
export class ImaginusSessionSearchComponent implements OnDestroy {
  @Input() teamCode: string | null = null;
  /** footer：下拉選單向上展開（固定於側欄底部時） */
  @Input() placement: 'inline' | 'footer' = 'inline';
  @Output() queryChange = new EventEmitter<string>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  query = '';
  dropdownOpen = false;
  loading = false;
  apiResults: ImaginusSessionSearchHit[] = [];

  private readonly querySubject = new Subject<string>();
  private searchSub?: Subscription;

  constructor(
    private imaginusService: ImaginusService,
    private router: Router,
    private locale: LocaleService,
  ) {
    this.searchSub = this.querySubject.pipe(
      debounceTime(280),
      distinctUntilChanged(),
      switchMap((q) => {
        const trimmed = (q || '').trim();
        if (trimmed.length < 1) {
          this.loading = false;
          return of({ results: [] as ImaginusSessionSearchHit[] });
        }
        this.loading = true;
        if (this.teamCode) {
          return this.imaginusService.search_team_sessions(this.teamCode, trimmed);
        }
        return this.imaginusService.search_unified_sessions(trimmed);
      }),
    ).subscribe({
      next: (res) => {
        this.loading = false;
        this.apiResults = res?.results || [];
        this.dropdownOpen = this.query.trim().length > 0;
      },
      error: () => {
        this.loading = false;
        this.apiResults = [];
        this.dropdownOpen = this.query.trim().length > 0;
      },
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  onQueryInput(): void {
    const q = this.query;
    this.queryChange.emit(q);
    this.querySubject.next(q);
    if (!q.trim()) {
      this.apiResults = [];
      this.dropdownOpen = false;
      this.loading = false;
    }
  }

  onFocus(): void {
    if (this.query.trim()) {
      this.dropdownOpen = true;
    }
  }

  clearQuery(): void {
    this.query = '';
    this.apiResults = [];
    this.dropdownOpen = false;
    this.loading = false;
    this.queryChange.emit('');
    this.querySubject.next('');
    this.searchInput?.nativeElement.focus();
  }

  openSession(hit: ImaginusSessionSearchHit): void {
    const sessionId = hit?.id;
    if (!sessionId) {
      return;
    }
    this.dropdownOpen = false;
    this.query = '';
    this.apiResults = [];
    this.queryChange.emit('');
    this.querySubject.next('');
    const queryParams: Record<string, string> = { session: sessionId };
    if (hit.scope === 'team' && hit.team_code) {
      queryParams['scope'] = 'team';
      queryParams['team_code'] = hit.team_code;
    } else {
      queryParams['scope'] = 'individual';
    }
    this.router.navigate(['/home'], { queryParams });
  }

  sessionTitle(name?: string): string {
    const fallback = this.locale.instant('imaginus.defaults.chatName');
    const n = (name || fallback).trim() || fallback;
    return n.length > 40 ? `${n.slice(0, 40)}…` : n;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.dropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const root = (event.target as HTMLElement)?.closest('.imaginus-session-search');
    if (!root) {
      this.dropdownOpen = false;
    }
  }
}
